import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, map, switchMap, take, tap} from 'rxjs/operators';
import * as cyberSourceActions from '../../store/actions/cyber-source.action';
import {
  CyberSourceActionTypes, DeviceDataCollectionFinished,
  Invoke3ds,
  PayerAuthEnrollment,
  Validate3ds
} from '../../store/actions/cyber-source.action';
import {CyberSourceService} from '@app/payment/services/cybersource/cyber-source.service';
import {DcpCheckoutPaymentService} from '@app/payment/services/dcp-checkout-payment.service';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {PaymentProvider} from '@app/payment/services/dcp-cc-psp.service';
import {Observable, of} from 'rxjs';
import {LAUNCH_CALLER, LaunchDialogService} from '@spartacus/storefront';
import {Cart} from '@spartacus/cart/base/root';
import {
  PayerAuthEnrollmentEntryData,
  PayerAuthEnrollmentResponse
} from '@app/model/cyber-source/payer-auth-enrollment-response';
import {PayerAuthSetupResponse} from '@app/model/cyber-source/payer-auth-setup-response';
import * as fromCyberSource from '@app/payment/services/store/reducers/cyber-source.reducer';
import {CyberSourcePaymentState} from '@app/payment/services/store/payment-state';
import {Store} from '@ngrx/store';

@Injectable()
export class CyberSourceEffects {

  constructor(private actions$: Actions,
              private cyberSourceService: CyberSourceService,
              private checkoutPaymentService: DcpCheckoutPaymentService,
              public dcpActiveCartService: DcpActiveCartService,
              private launchDialogService: LaunchDialogService,
              private store: Store,
  ) {
  }

  startPaymentFlow$ = createEffect((): Observable<any> =>
    this.actions$.pipe(
      ofType(CyberSourceActionTypes.StartPaymentFlow),
      switchMap(_ => this.dcpActiveCartService.getActive().pipe(take(1))),
      switchMap(cart => {
        const {vendor, salesOrg} = this.checkoutPaymentService.getVendorSalesOrg(cart);
        return this.checkoutPaymentService.getCardConfig(vendor, salesOrg, PaymentProvider.CYBERSOURCE);
      }),
      map(cardConfig => cardConfig['3DSEnable']
        ? new cyberSourceActions.PayerAuthSetup()
        : new cyberSourceActions.PayerAuthEnrollment()),
      catchError(error => of(new cyberSourceActions.GenericError(error))),
    )
  );

  payerAuthSetup$ = createEffect((): Observable<any> =>
    this.actions$.pipe(
      ofType(CyberSourceActionTypes.PayerAuthSetup),
      switchMap(_ => this.cyberSourceService.getPayerAuthSetupOnPlaceOrder()),
      map((response: PayerAuthSetupResponse) => new cyberSourceActions.DeviceDataCollection(response.consumerAuthenticationInformation)),
      catchError(error => of(new cyberSourceActions.GenericError(error))),
    )
  );

  deviceDataCollectionFinished$ = createEffect((): Observable<any> =>
    this.actions$.pipe(
      ofType(CyberSourceActionTypes.DeviceDataCollectionFinished),
      tap((result: DeviceDataCollectionFinished) => this.referenceId = result.referenceId),
      map(result => new cyberSourceActions.PayerAuthEnrollment(this.referenceId)),
    )
  );

  payerAuthEnrollment$ = createEffect((): Observable<any> =>
    this.actions$.pipe(
      ofType(CyberSourceActionTypes.PayerAuthEnrollment),
      switchMap((a: PayerAuthEnrollment) => this.cyberSourceService.getPayerAuthEnrollmentOnPlaceOrder(a.referenceId)),
      map((response: PayerAuthEnrollmentResponse) => {
        const status = response.paymentResponse[0].value.status;
        switch (status) {
          case 'PENDING_AUTHENTICATION':
            return new cyberSourceActions.Invoke3ds(response);
          case 'AUTHORIZED':
            return new cyberSourceActions.PaymentSuccess(response);
          default:
            return new cyberSourceActions.PaymentFailure();
        }
      }),
      catchError(error => of(new cyberSourceActions.GenericError(error))),
    )
  );

  invoke3ds$ = createEffect((): Observable<any> =>
      this.actions$.pipe(
        ofType(CyberSourceActionTypes.Invoke3ds),
        switchMap(action => this.dcpActiveCartService.getActive().pipe(
          take(1),
          map(cart => {
            return {cart, action};
          }))),
        tap(({cart, action}: { cart: Cart, action: Invoke3ds }) =>
          this.launchDialogService.openDialogAndSubscribe(LAUNCH_CALLER.CYBERSOURCE_3DS_IFRAME, null, {
            consumerAuthenticationInformation: action.payerAuthEnrollmentResponse.paymentResponse[0].value.consumerAuthenticationInformation,
            cartId: cart.code,
          })
        ) as any,
      ),
    {dispatch: false}
  );

  referenceId: string;
  transactionId: string;
  state: CyberSourcePaymentState;

  validate3ds$ = createEffect((): Observable<any> =>
    this.actions$.pipe(
      ofType(CyberSourceActionTypes.Validate3ds),
      tap((action: Validate3ds) => this.transactionId = action.transactionId),
      switchMap((action: Validate3ds) => this.store.select(fromCyberSource.getState).pipe(take(1))),
      switchMap((state: CyberSourcePaymentState) => this.cyberSourceService.getPayerAuthEnrollmentOnPlaceOrder(this.referenceId, this.transactionId).pipe(
        tap(a => this.state = state)
      )),
      map((response: PayerAuthEnrollmentResponse) => {
        const validationResult: PayerAuthEnrollmentEntryData = response.paymentResponse[0].value;
        if (validationResult.status === 'AUTHORIZED') {
          return new cyberSourceActions.PaymentSuccess(response);
        }
        return new cyberSourceActions.ChallengeFailure();
      }) as any,
      catchError(error => of(new cyberSourceActions.GenericError(error))),
    )
  );

}
